/**
 * MockWsClient — a drop-in stand-in for the real `wsClient` singleton in the
 * simulated build. It opens no socket; instead DemoSimulation pushes frames
 * through `emit()`, which fan out to the same `onMessage` handlers the real
 * client uses. It mirrors WsClient's public surface so consumers (WsEventSource,
 * ConnectionStatus, RootLayout) work unchanged, and reports "connected" so the
 * UI reads as live.
 */

type MessageHandler = (data: unknown) => void;
type ConnectionState = "connected" | "reconnecting" | "disconnected";
type StatusHandler = (status: ConnectionState) => void;

export class MockWsClient {
  private handlers: MessageHandler[] = [];
  private statusHandlers: StatusHandler[] = [];
  private connectionState: ConnectionState = "disconnected";

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  connect(): void {
    this.setConnectionState("connected");
  }

  onMessage(handler: MessageHandler): () => void {
    this.handlers.push(handler);
    return () => {
      this.handlers = this.handlers.filter((h) => h !== handler);
    };
  }

  onStatusChange(handler: StatusHandler): () => void {
    this.statusHandlers.push(handler);
    return () => {
      this.statusHandlers = this.statusHandlers.filter((h) => h !== handler);
    };
  }

  // The real client sends these upstream; in the mock they are inert.
  subscribe(_filter: { topologyId?: string; runId?: string; types?: string[] }): void {}
  sendInputResponse(_runId: string, _feedbackMessageId: string, _value: unknown): void {}

  close(): void {
    this.setConnectionState("disconnected");
  }

  /** Push a wire frame to every registered message handler. */
  emit(frame: unknown): void {
    for (const handler of this.handlers) {
      handler(frame);
    }
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    for (const handler of this.statusHandlers) {
      handler(state);
    }
  }
}
